import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowDown,
  Quote,
  Check,
  X,
  Calculator,
  BookOpen,
  ShieldCheck,
  TrendingUp,
  Wallet,
  Users,
  Award,
  Newspaper,
  HeartHandshake,
  Menu,
  XIcon,
} from "lucide-react";
import Logo from "../components/site/Logo";
import Reveal, { RevealStagger, RevealItem } from "../components/v2/Reveal";
import ParallaxBg from "../components/v2/ParallaxBg";
import {
  CLIENT_LOGOS,
  PARTNER_LOGOS,
  MEDIA_COVERS,
  AWARD_BADGES,
} from "../components/v2/BrandLogos";

// Contact form destination — not displayed as text on the page.
// Backend base — the contact endpoint on the FastAPI backend.
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ============ SHARED MEDIA ============
const IMG = {
  heroCity:
    "https://images.pexels.com/photos/2539053/pexels-photo-2539053.jpeg",
  cityNight:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=2000&q=70",
  ocean:
    "https://images.unsplash.com/photo-1505142468610-359e7d316be0?auto=format&fit=crop&w=2000&q=70",
  buildingMod:
    "https://images.pexels.com/photos/7230895/pexels-photo-7230895.jpeg",
  chess:
    "https://images.unsplash.com/photo-1560174038-da43ac74f01b?auto=format&fit=crop&w=1800&q=70",
  justice:
    "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1800&q=70",
  hospitality: "/img/team-office.jpg",
  sailingShip: "/img/sailing-ship.jpg",
  documents:
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1800&q=70",
  bachir:
    "https://customer-assets.emergentagent.com/job_slide-to-web-3/artifacts/v68iqi4q_TheTaxFactor-BrighterEdit-10.jpg",
  sumit:
    "https://customer-assets.emergentagent.com/job_slide-to-web-3/artifacts/ai89xxqk_Copilot_20260501_185057.png",
  collab:
    "https://images.unsplash.com/photo-1758518725921-1eb74ed293be",
};

// ============ NAVBAR ============
const NavbarV2 = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#about", "About"],
    ["#solutions", "Solutions"],
    ["#process", "Process"],
    ["#team", "Team"],
    ["#contact", "Contact"],
  ];

  return (
    <header
      data-testid="v2-navbar"
      data-logo-bg={scrolled ? "light" : "dark"}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-[88px]">
        <a href="#top" data-testid="v2-nav-logo" aria-label="Envisio Advisory">
          <Logo height={56} />
        </a>
        <div className="hidden md:flex items-center gap-10">
          {links.map(([href, label]) => (
            <a
              key={href}
              href={href}
              data-testid={`v2-nav-${label.toLowerCase()}`}
              className={`v2-eyebrow text-[11px] hover:text-[#3B82F6] transition-colors ${
                scrolled ? "text-[#0A2540]" : "text-white/85"
              }`}
            >
              {label}
            </a>
          ))}
        </div>
        <a
          href="#contact"
          data-testid="v2-nav-cta"
          className={`hidden md:inline-flex v2-btn !py-3 !px-5 ${
            scrolled ? "v2-btn-solid" : "v2-btn-outline-light"
          }`}
        >
          Let's Talk
          <ArrowRight size={14} />
        </a>
        <button
          className={`md:hidden inline-flex items-center justify-center w-11 h-11 border ${
            scrolled
              ? "border-slate-300 text-[#0A2540]"
              : "border-white/40 text-white"
          }`}
          onClick={() => setOpen(!open)}
          data-testid="v2-nav-toggle"
          aria-label="menu"
        >
          {open ? <XIcon size={20} /> : <Menu size={20} />}
        </button>
      </nav>
      {open && (
        <div className="md:hidden bg-white border-t border-slate-200" data-logo-bg="light">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map(([h, l]) => (
              <a
                key={h}
                href={h}
                onClick={() => setOpen(false)}
                className="v2-eyebrow text-[#0A2540] text-[12px] py-1.5"
              >
                {l}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="v2-btn v2-btn-solid w-full justify-center mt-2"
            >
              Let's Talk
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ============ SLIDE 01 — HERO ============
const HeroV2 = () => (
  <section
    id="top"
    data-testid="v2-hero"
    className="relative min-h-[100svh] bg-[#0A2540] text-white overflow-hidden"
  >
    <ParallaxBg src={IMG.heroCity} speed={0.22} position="center center" />
    <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/95 via-[#0A2540]/80 to-[#0A2540]/95" />
    <div className="absolute inset-0 ea-grain" aria-hidden />

    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-[120px] md:pt-[160px] pb-20 min-h-[100svh] flex flex-col justify-between">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.2, 0.6, 0.2, 1] }}
      >
        <div className="flex items-center justify-between mb-12">
          <div className="v2-eyebrow text-white/60">
            <span className="v2-rule" />
            Award-winning · Australia-wide
          </div>
        </div>

        <h1 className="v2-h1 text-white">
          YOUR
          <br />
          <span className="text-white/40">VIRTUAL</span> CFO.
        </h1>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <p className="v2-body-lg md:col-span-6 text-white/80 max-w-xl">
          Envisio Advisory is an award-winning Australian virtual CFO practice.
          We partner with founders to provide clarity, confidence, and control
          in every financial decision in scaling your enterprise/business.
        </p>
          <div className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
            <a
              href="#contact"
              data-testid="v2-hero-cta-primary"
              className="v2-btn v2-btn-light"
            >
              Book a Consultation
              <ArrowRight size={14} />
            </a>
            <a
              href="#solutions"
              data-testid="v2-hero-cta-secondary"
              className="v2-btn v2-btn-outline-light"
            >
              Explore Our Services
            </a>
          </div>
        </div>
      </motion.div>

      {/* Professional credentials — CPA Australia + IPA */}
      <div
        data-testid="v2-hero-credentials"
        className="mt-14 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8"
      >
        <div className="v2-eyebrow text-white/50 text-[10px] inline-flex items-center gap-3">
          <span className="v2-rule" />
          Accredited Members
        </div>
        <div className="flex items-center gap-5 md:gap-6">
          <img
            src="/img/credentials/cpa-australia.webp"
            alt="CPA Australia — Certified Practising Accountants"
            loading="lazy"
            className="h-20 md:h-24 w-auto object-contain"
          />
          <img
            src="/img/credentials/ipa-dark.png"
            alt="Institute of Public Accountants (IPA)"
            loading="lazy"
            className="h-20 md:h-24 w-auto object-contain"
          />
        </div>
      </div>

      <div className="mt-10 flex items-center gap-3 text-white/55">
        <ArrowDown size={16} className="animate-bounce" />
        <span className="v2-eyebrow text-white/55 text-[10px]">
          Discover Envisio
        </span>
      </div>
    </div>
  </section>
);

// ============ SLIDE 02 — QUOTE ============
const QuoteV2 = () => (
  <section
    data-testid="v2-quote"
    className="relative bg-[#0A2540] text-white py-32 md:py-44 overflow-hidden"
  >
    <ParallaxBg src={IMG.buildingMod} speed={0.15} className="opacity-30" />
    <div className="absolute inset-0 bg-[#0A2540]/60" />
    <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <Quote
          className="text-[#3B82F6] mx-auto mb-12"
          size={56}
          strokeWidth={1.2}
        />
      </Reveal>
      <Reveal delay={0.1} y={40}>
        <p className="v2-display text-white text-[clamp(2rem,5vw,4rem)] leading-[1.05]">
          "PROFESSIONAL. RESPONSIVE. ATTENTIVE.
          <br />
          <span className="text-white/65">
            THE PLACE TO GO FOR THE BEST RESULTS
          </span>
          <br />
          AND PEACE OF MIND."
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-12 flex items-center justify-center gap-4">
          <span className="w-10 h-px bg-white/40" />
          <div className="text-left">
            <div className="font-semibold text-[14px] text-white">
              Houssam A
            </div>
            <div className="v2-eyebrow text-[10px] text-white/55">
              Director · Getitaway Demolition
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

// ============ SLIDE 03 — ABOUT ============
const AboutV2 = () => (
  <section
    id="about"
    data-testid="v2-about"
    className="relative bg-black text-white py-32 md:py-48 overflow-hidden"
  >
    <ParallaxBg src={IMG.cityNight} speed={0.2} className="opacity-50" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex justify-between items-baseline mb-12">
        <div className="v2-eyebrow text-white/60">
          <span className="v2-rule" /> About Envisio Advisory
        </div>
      </div>

      <h2 className="v2-h2 text-white max-w-5xl">
        ENVISIO ADVISORY IS AN
        <br />
        <span className="text-white/45">AWARD-WINNING TEAM</span>
        <br />
        OF HIGHLY SKILLED ACCOUNTING
        <br />
        AND BUSINESS PROFESSIONALS.
      </h2>
      <p className="v2-body-lg mt-12 max-w-2xl text-white/70">
        With decades of combined industry experience, we operate as your
        virtual CFO — strategic, proactive, commercial.
      </p>
    </div>
  </section>
);

// ============ SLIDE 04 — STATS ============
const StatsV2 = () => (
  <section
    data-testid="v2-stats"
    className="bg-white"
  >
    <Reveal y={40}>
      <div
        data-testid="v2-stat-experience"
        className="relative min-h-[520px] md:min-h-[680px] flex items-end p-10 md:p-16 overflow-hidden text-white"
      >
        <ParallaxBg src={IMG.buildingMod} speed={0.18} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/55 to-transparent" />
        <div className="relative max-w-3xl">
          <div className="v2-num text-white">40</div>
          <div className="v2-display text-[40px] md:text-[64px] text-white/90 mt-2">
            YEARS OF COMBINED EXPERIENCE
          </div>
          <div className="v2-body text-white/80 mt-6 max-w-2xl">
            Decades of tax, accounting and advisory expertise sitting alongside
            you — from Big Four training and industry CFO leadership to
            hands-on practice ownership.
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

// ============ SLIDE 05 — ONE FIRM, MANY SOLUTIONS (HUB & SPOKE) ============
const OneFirmV2 = () => {
  const items = [
    { icon: Calculator, label: "BUSINESS STRATEGY" },
    { icon: BookOpen, label: "ACCOUNTING" },
    { icon: ShieldCheck, label: "AUDITING" },
    { icon: TrendingUp, label: "ADVISORY" },
    { icon: Wallet, label: "FINANCE" },
    { icon: Users, label: "PAYROLL" },
  ];

  return (
    <section
      id="solutions"
      data-testid="v2-onefirm"
      className="bg-[#F4F1EA] text-[#0A2540] py-24 md:py-36 overflow-x-clip"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> One Firm · Many Solutions
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <h2 className="v2-h2">
              ONE FIRM.
              <br />
              <span className="text-[#0A2540]/35">MANY</span>
              <br />
              SOLUTIONS.
            </h2>
            <p className="v2-body text-[#0A2540]/75 mt-8 max-w-md">
              An all-in-one virtual CFO service.
            </p>
          </Reveal>

          {/* MOBILE: clean 2×3 icon grid (no hub-and-spoke to avoid crowding) */}
          <div className="lg:hidden lg:col-span-7 grid grid-cols-2 gap-3 w-full">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  data-testid={`v2-mobile-service-${it.label.toLowerCase()}`}
                  className="bg-white border border-[#0A2540]/15 p-4 flex flex-col items-center justify-center text-center gap-3 min-h-[120px]"
                >
                  <div className="w-11 h-11 shrink-0 bg-[#0A2540] text-white rounded-full flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <div className="text-[11px] font-bold tracking-[0.1em] uppercase text-[#0A2540] leading-tight break-words w-full">
                    {it.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP: Hub & Spoke diagram - animated entrance (ORIGINAL values, do not change) */}
          <div className="hidden lg:flex lg:col-span-7 items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: -15 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 1.4,
                ease: [0.2, 0.6, 0.2, 1],
              }}
              className="relative w-full max-w-[640px] aspect-square"
            >
              {/* outer ring */}
              <motion.div
                className="absolute inset-[6%] border border-[#0A2540]/20 rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
              />
              <div className="absolute inset-[18%] border border-[#0A2540]/15 rounded-full" />
              {/* center */}
              <motion.div
                className="absolute inset-[30%] bg-[#0A2540] rounded-full flex flex-col items-center justify-center text-white shadow-[0_30px_60px_-20px_rgba(10,37,64,0.4)]"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <div className="v2-display text-[28px] md:text-[40px] leading-none text-center px-3">
                  ONE
                  <br />
                  FIRM
                </div>
              </motion.div>
              {/* spokes */}
              {items.map((it, i) => {
                const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2;
                const x = 50 + 44 * Math.cos(angle);
                const y = 50 + 44 * Math.sin(angle);
                const Icon = it.icon;
                return (
                  <motion.div
                    key={it.label}
                    initial={{ opacity: 0, scale: 0.4 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                      delay: 0.4 + i * 0.08,
                      duration: 0.6,
                      ease: [0.2, 0.6, 0.2, 1],
                    }}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${x}%`, top: `${y}%` }}
                    data-testid={`v2-spoke-${it.label.toLowerCase()}`}
                  >
                    <div className="flex flex-col items-center gap-2 group cursor-default">
                      <motion.div
                        whileHover={{ scale: 1.1, y: -2 }}
                        className="w-14 h-14 md:w-20 md:h-20 bg-white border border-[#0A2540]/20 rounded-full flex items-center justify-center text-[#0A2540] group-hover:bg-[#0A2540] group-hover:text-white transition-colors"
                      >
                        <Icon size={20} strokeWidth={1.5} />
                      </motion.div>
                      <div className="v2-eyebrow text-[10px] text-[#0A2540]">
                        {it.label}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ SLIDE 06 — NAVIGATORS (sailing ship parallax) ============
const NavigatorsV2 = () => (
  <section
    data-testid="v2-navigators"
    className="relative bg-[#0A2540] text-white py-32 md:py-44 overflow-hidden min-h-[640px]"
  >
    {/* Sailing ship background — flipped so the boat sits on the RIGHT half (clear of the headline) */}
    <ParallaxBg
      src={IMG.sailingShip}
      speed={0.22}
      position="center center"
      size="cover"
      flipX
      className="opacity-95"
    />
    {/* Strong blue veil over the LEFT (text area), softened on the right so the boat stays visible */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(90deg, rgba(10,37,64,0.97) 0%, rgba(10,37,64,0.92) 32%, rgba(10,37,64,0.55) 60%, rgba(10,37,64,0.45) 100%)",
      }}
    />
    {/* Tint overlay to keep the right half blue-ish even where the image shows */}
    <div className="absolute inset-0 bg-[#0A2540]/15 mix-blend-multiply" />
    {/* Subtle vignette */}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/55 via-transparent to-[#0A2540]/30" />

    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-white/65">
            <span className="v2-rule" /> Our Approach
          </div>
        </div>
      </Reveal>
      {/* Constrain text to the LEFT half so the ship is visible on the right (desktop); on mobile, full width */}
      <div className="max-w-full md:max-w-[55%] lg:max-w-[50%]">
        <Reveal delay={0.1} y={40}>
          <h2 className="v2-h2 text-white">
            YOUR BUSINESS
            <br />
            <span className="text-[#3B82F6]">NAVIGATORS.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="v2-body-lg mt-10 text-white/85">
            We help our clients think beyond just tax — and focus on what
            actually matters:{" "}
            <span className="text-white font-medium">
              growing their business
            </span>
            ,{" "}
            <span className="text-white font-medium">
              navigating challenges
            </span>{" "}
            and{" "}
            <span className="text-white font-medium">
              capitalising on new opportunities
            </span>
            .
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

// ============ SLIDE 07 — FAVOURITE CLIENTS (with logo cards) ============
const ClientsV2 = () => (
  <section
    data-testid="v2-clients"
    className="bg-white py-24 md:py-36 border-t border-b border-slate-200"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> Client Portfolio
          </div>
        </div>
        <h2 className="v2-h2 text-[#0A2540] mb-4">
          OUR <span className="text-[#0A2540]/35">FAVOURITE</span> CLIENTS.
        </h2>
        <p className="v2-body text-[#0A2540]/75 max-w-2xl mb-16">
          From hospitality and NDIS to construction, retail and professional
          services — we bring wealth of insight across every industry we
          serve.
        </p>
      </Reveal>

      <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CLIENT_LOGOS.map((LogoCard, i) => (
          <RevealItem key={i}>
            <LogoCard />
          </RevealItem>
        ))}
      </RevealStagger>
    </div>
  </section>
);

// ============ SLIDE 08 — AWARDS (badge cards) ============
const AwardsV2 = () => (
  <section
    data-testid="v2-awards"
    className="bg-[#0A2540] text-white py-24 md:py-36"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-white/60">
            <span className="v2-rule" /> Awards &amp; Recognition
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <h2 className="v2-h2 lg:col-span-7 text-white">
            WINNERS &amp;
            <br />
            NOMINEES FOR
            <br />
            <span className="text-[#3B82F6]">NUMEROUS AWARDS.</span>
          </h2>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <p className="v2-body text-white/80 max-w-md">
              Every year nominated or victorious — recognition for exceptional
              service, including the IPA Accounting Practice of the Year
              several times.
            </p>
          </div>
        </div>
      </Reveal>

      <RevealStagger className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {AWARD_BADGES.map((Badge, i) => (
          <RevealItem key={i}>
            <Badge />
          </RevealItem>
        ))}
      </RevealStagger>
    </div>
  </section>
);

// ============ SLIDE 09 — MEDIA FEATURES (magazine covers) ============
const MediaV2 = () => (
  <section data-testid="v2-media" className="bg-[#F4F1EA] py-24 md:py-36">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> Press &amp; Publications
          </div>
        </div>
        <h2 className="v2-h2 text-[#0A2540] mb-4 max-w-4xl">
          WHERE WE'VE <span className="text-[#0A2540]/35">FEATURED.</span>
        </h2>
        <p className="v2-body text-[#0A2540]/75 max-w-xl mb-16">
          As keynote speakers and media features across the accounting
          industry — from IPA's Public Accountant to CPA Australia's In The
          Black, TAXPO Sydney and the World Congress of Accountants.
        </p>
      </Reveal>

      {/* MOBILE: real magazine feature images */}
      <div className="grid grid-cols-2 gap-4 lg:hidden">
        {[
          { src: "img/media/publicaccountant.png", alt: "Public Accountant — IPA cover feature" },
          { src: "img/media/intheblack.png", alt: "In The Black — CPA Australia feature" },
          { src: "img/media/taxpo.png", alt: "TAXPO 2022 Sydney — keynote speakers" },
          { src: "img/media/wcoa.png", alt: "World Congress of Accountants 2018" },
        ].map((m, i) => (
          <div
            key={i}
            data-testid={`v2-media-mobile-${i}`}
            className="aspect-[3/4] bg-[#0A2540] overflow-hidden flex items-center justify-center"
          >
            <img
              src={m.src}
              alt={m.alt}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* DESKTOP: designed magazine-cover cards */}
      <RevealStagger className="hidden lg:grid grid-cols-4 gap-6">
        {MEDIA_COVERS.map((Cover, i) => (
          <RevealItem key={i}>
            <Cover />
          </RevealItem>
        ))}
      </RevealStagger>
    </div>
  </section>
);

// ============ SLIDE 10 — PARTNERS (logo cards) ============
const PartnersV2 = () => {
  const pillars = [
    "Charitable causes",
    "Large community events",
    "Professional forums",
    "Industry associations",
  ];
  return (
    <section
      data-testid="v2-partners"
      className="bg-white py-24 md:py-32 border-t border-slate-200"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex justify-between items-baseline mb-12">
            <div className="v2-eyebrow text-[#0A2540]/60">
              <span className="v2-rule" /> Community Partnerships
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <HeartHandshake
              size={48}
              strokeWidth={1.2}
              className="text-[#3B82F6] mb-4"
            />
            <h2 className="v2-h2 text-[#0A2540]">
              PARTNERS
              <br />
              <span className="text-[#0A2540]/35">IN POSITIVE.</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <p className="v2-body-lg text-[#0A2540]/85 max-w-2xl">
              We proudly and regularly sponsor charitable causes, large
              community events, professional forums and more — because being a
              trusted advisor extends well beyond the boardroom.
            </p>
            <ul
              data-testid="v2-partner-pillars"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4"
            >
              {pillars.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 text-[15px] md:text-[16px] text-[#0A2540]"
                >
                  <span className="mt-[7px] shrink-0 w-2 h-2 bg-[#3B82F6] inline-block" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ============ SLIDE 11 — TESTIMONIALS (SPEECH BUBBLES) ============
const TestimonialsV2 = () => {
  const t = [
    {
      q: "AS A CLIENT FOR 13+ YEARS, I HIGHLY RECOMMEND.",
      n: "James R",
      r: "Entrepreneur",
    },
    {
      q: "PROFESSIONAL, TRUSTWORTHY AND RESULTS DRIVEN.",
      n: "Suleiman K",
      r: "Private Blends",
    },
    {
      q: "THE BEST IN THE BUSINESS.",
      n: "Tarik Houchar",
      r: "Founder, Hijab House",
    },
  ];
  return (
    <section
      data-testid="v2-testimonials"
      className="bg-[#0A2540] text-white py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-white/60">
            <span className="v2-rule" /> Client Testimonials
          </div>
        </div>
        <h2 className="v2-h2 text-white mb-4 max-w-4xl">
          WHAT OUR CLIENTS
          <br />
          <span className="text-[#3B82F6]">SAY.</span>
        </h2>
        <p className="v2-body text-white/75 max-w-xl mb-16">
          Testimonials from founders and operators we have partnered with
          across Australia — spanning hospitality, retail, disability care,
          construction and beyond.
        </p>
        <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {t.map((it, i) => (
            <RevealItem key={i}>
              <div data-testid={`v2-testi-${i}`}>
                <div className="v2-bubble shadow-2xl">
                  <div className="v2-display text-[#0A2540] text-[26px] md:text-[30px] leading-[1.1]">
                    "{it.q}"
                  </div>
                </div>
                <div className="mt-10 ml-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#3B82F6] flex items-center justify-center font-bold text-[15px]">
                    {it.n.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-[15px]">{it.n}</div>
                    <div className="v2-eyebrow text-white/70 text-[11px]">
                      {it.r}
                    </div>
                  </div>
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
};

// ============ SLIDE 12 — TEAM ============
const TeamV2 = () => {
  const team = [
    {
      name: "BACHIR ZREIKA",
      role: "CEO",
      img: IMG.bachir,
      points: [
        "20+ YEARS' EXPERIENCE",
        "AWARD-WINNING ADVISOR",
        "KEYNOTE SPEAKER",
        "IPA BOARD MEMBER",
      ],
    },
    {
      name: "SUMIT CHUGANI",
      role: "CFO",
      img: IMG.sumit,
      points: [
        "17+ YEARS' EXPERIENCE",
        "FCPA · FMCG INDUSTRY",
        "FINANCIAL MODELLER",
        "TOP ALLIANZ 20 NDIS",
      ],
    },
  ];
  return (
    <section
      id="team"
      data-testid="v2-team"
      className="bg-[#F4F1EA] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> Meet the Team
          </div>
        </div>
        <h2 className="v2-h2 text-[#0A2540] mb-4">MEET THE TEAM.</h2>
        <p className="v2-eyebrow text-[#3B82F6] mb-8">
          Your new Envisio advisors
        </p>
        <p className="v2-body text-[#0A2540]/75 max-w-2xl mb-16">
          Decades of expertise at a fraction of the cost of a full-time
          executive hire — suited to businesses of any size, in any industry.
        </p>

        <RevealStagger
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[1100px]"
          stagger={0.15}
        >
          {team.map((m, i) => (
            <RevealItem key={m.name}>
              <article
                data-testid={`v2-team-${i}`}
                className="bg-[#0A2540] text-white overflow-hidden group"
              >
                <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#F4F1EA] v2-img-hover">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: "center 18%" }}
                    loading="lazy"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <div className="v2-eyebrow text-[#3B82F6] text-[12px] mb-3">
                    {m.role}
                  </div>
                  <h3 className="v2-display text-[42px] md:text-[56px] text-white leading-none">
                    {m.name}
                  </h3>
                  <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                    {m.points.map((p) => (
                      <li
                        key={p}
                        className="v2-eyebrow text-white/90 text-[12px] flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 bg-[#3B82F6] inline-block shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
};

// ============ SLIDE 13 — METRICS ============
const MetricsV2 = () => (
  <section
    data-testid="v2-metrics"
    className="bg-white py-24 md:py-36"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex justify-between items-baseline mb-12">
        <div className="v2-eyebrow text-[#0A2540]/60">
          <span className="v2-rule" /> The Metrics That Matter
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
        <Reveal y={40}>
          <div className="border-t-4 border-[#3B82F6] pt-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, ease: [0.2, 0.6, 0.2, 1] }}
              className="v2-num text-[#0A2540]"
            >
              15<span className="text-[#0A2540]/30">%</span>
            </motion.div>
            <div className="v2-display text-[#0A2540] text-[26px] md:text-[34px] mt-6 leading-[1.05]">
              CLIENT COST
              <br />
              REDUCTION.
            </div>
            <p className="v2-body text-[#0A2540]/75 mt-6 max-w-md">
              Plus 10–15% reduction in client foreign exchange loss exposure
              through hedging and active strategy.
            </p>
          </div>
        </Reveal>
        <Reveal y={40} delay={0.15}>
          <div className="border-t-4 border-[#0A2540] pt-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 1,
                delay: 0.15,
                ease: [0.2, 0.6, 0.2, 1],
              }}
              className="v2-num text-[#0A2540]"
            >
              20<span className="text-[#0A2540]/30">%</span>
            </motion.div>
            <div className="v2-display text-[#0A2540] text-[26px] md:text-[34px] mt-6 leading-[1.05]">
              CLIENT REVENUE
              <br />
              GROWTH.
            </div>
            <p className="v2-body text-[#0A2540]/75 mt-6 max-w-md">
              Average increase in client revenue, growth and profitability across
              engagements — measured, tracked and reported monthly.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// ============ SLIDE 14 — BENEFITS / NEXT LEVEL ============
const BenefitsV2 = () => {
  const items = [
    "NEVER MISS ANOTHER OPPORTUNITY.",
    "ONE ESSENTIAL SERVICE FOR ALL FINANCIAL MATTERS.",
    "SUITED TO ANY BUSINESS, ANY SIZE, ANY INDUSTRY.",
    "DECADES OF EXPERTISE AT A FRACTION OF THE COST.",
    "REFINE YOUR BUSINESS MODEL · BECOME FUTURE PROOF.",
  ];
  return (
    <section
      data-testid="v2-benefits"
      className="bg-[#0A2540] text-white py-24 md:py-36 relative overflow-hidden"
    >
      <div className="absolute inset-0 ea-grain opacity-50" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-white/60">
            <span className="v2-rule" /> Why Choose Envisio
          </div>
        </div>
        <h2 className="v2-h2 text-white mb-16 max-w-5xl">
          TAKE YOUR BUSINESS
          <br />
          TO THE NEXT LEVEL.
          <br />
          <span className="text-[#3B82F6]">LONG-TERM. SUSTAINABLE.</span>
        </h2>        <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {items.map((it, i) => (
            <RevealItem key={i}>
              <li
                data-testid={`v2-benefit-${i}`}
                className="bg-[#0A2540] p-8 md:p-10 flex items-center gap-5 group hover:bg-[#0E2E52] transition-colors h-full"
              >
                <div className="w-10 h-10 bg-[#3B82F6] flex items-center justify-center shrink-0">
                  <Check size={18} strokeWidth={3} />
                </div>
                <div className="v2-display text-[22px] md:text-[28px] text-white leading-[1.1]">
                  {it}
                </div>
              </li>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
};

// ============ SLIDE 15 — WHAT WE DO (CYCLE DIAGRAM) ============
const WhatWeDoV2 = () => {
  const quads = [
    {
      label: "ADVISORY",
      sub: "Performance metrics, strategy, sustainability.",
      icon: TrendingUp,
    },
    {
      label: "STAFFING & PAYROLL",
      sub: "Payroll · timely processing · compliance · super.",
      icon: Users,
    },
    {
      label: "ADD-ON SERVICES",
      sub: "Costing, inventory control, system setup, financial modelling.",
      icon: Wallet,
    },
    {
      label: "BUSINESS STRATEGY",
      sub: "Sales, business valuations, building value, scaling up.",
      icon: ShieldCheck,
    },
  ];
  return (
    <section
      id="process"
      data-testid="v2-whatwedo"
      className="bg-[#F4F1EA] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> Our Services Framework
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <h2 className="v2-h2 text-[#0A2540]">
              WHAT WE DO.
            </h2>
            <p className="v2-body text-[#0A2540]/75 mt-8 max-w-md">
              Everything you need — all in one place. Four interconnected
              pillars that feed each other so strategy, compliance and
              operations never drift out of sync.
            </p>
          </div>

          {/* Cycle diagram - responsive: 1 col stacked on mobile, symmetric 2x2 on desktop */}
          <div className="lg:col-span-7 relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {quads.map((q, i) => {
                const Icon = q.icon;
                return (
                  <div
                    key={q.label}
                    data-testid={`v2-quad-${i}`}
                    className="bg-[#0A2540] text-white p-6 md:p-8 min-h-[220px] md:min-h-[280px] flex flex-col justify-between gap-6 group hover:bg-[#3B82F6] transition-colors cursor-default"
                  >
                    <div className="flex items-start justify-between">
                      <Icon
                        size={28}
                        strokeWidth={1.2}
                        className="text-[#3B82F6] group-hover:text-white"
                      />
                      <span className="v2-eyebrow text-white/35 text-[10px]">
                        0{i + 1}
                      </span>
                    </div>
                    <div>
                      <div className="v2-display text-[22px] md:text-[26px] leading-[1.1]">
                        {q.label}
                      </div>
                      <p className="text-[15px] md:text-[16px] text-white/85 group-hover:text-white/95 mt-3 leading-relaxed">
                        {q.sub}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* center pin — desktop only (no overlap risk on stacked mobile) */}
            <div className="absolute inset-0 hidden md:flex items-center justify-center pointer-events-none">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#F4F1EA] border-4 border-[#0A2540] flex items-center justify-center shadow-lg">
                <div className="v2-display text-[#0A2540] text-[16px] md:text-[18px] text-center leading-tight">
                  ALL IN
                  <br />
                  ONE
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ SLIDE 14b — SERVICE SPOTLIGHTS (deck imagery) ============
const ServiceSpotlightsV2 = () => {
  const spots = [
    {
      kicker: "ADVISORY & STRATEGY",
      title: "BUILD VALUE.\nSCALE UP.",
      body: "Performance metrics, business planning, KPI reporting and forecasting that turn intuition into a system.",
      img: IMG.chess,
      label: "MOVES THAT MATTER",
      bullets: [
        "Risk assessment (analysing gaps and mitigation)",
        "Preparing a Business Plan",
        "Organisation Structure",
        "Structure the books of Accounts",
        "Monthly Profit & Loss, Cashflow (with graphical representation & analysis)",
        "Budgeting and Forecasting · KPI Reporting",
        "Identify cost-savings within your company",
        "Internal Audits (overseeing internal bookkeeper)",
        "Audit of Systems and Processes",
        "Business Advisory, Sales & Valuations",
        "Interventions & recommendations",
      ],
      addons: [
        "Financial Modelling",
        "Costings",
        "Invoicing System",
        "Stock Control",
        "Accounts Receivable & Payable",
      ],
    },
    {
      kicker: "COMPLIANCE",
      title: "STAY ON TOP\nOF LEGISLATION.",
      body: "ATO liaison and audit compliance. We handle change so you can plan ahead.",
      img: IMG.justice,
      label: "PRECISION & PROOF",
      bullets: [
        "Company Structure",
        "Yearly Financials · ATO",
        "Liaising with the ATO",
        "Interim Financials (as and when required)",
        "Audit Compliance",
        "Recommendations on record keeping",
      ],
    },
    {
      kicker: "STAFFING & PAYROLL",
      title: "MANAGING\nYOUR OBLIGATIONS.",
      body: "Payroll/STP, super, awards & leave audits. Compliance you don't have to think about.",
      img: IMG.hospitality,
      label: "PEOPLE FIRST",
      bullets: [
        "Payroll Setup",
        "Payroll/STP Lodgments",
        "Superannuation",
        "Workers Compensation",
        "Payment Batch to the bank",
        "Payroll Audits (awards, hours, leaves)",
        "Payroll Risk and Cost Management",
        "Uplus and ACRIT (where required)",
        "Interventions and Recommendations",
        "Payroll compliance",
      ],
    },
  ];
  return (
    <section
      data-testid="v2-spotlights"
      className="bg-white py-24 md:py-36 border-t border-slate-200"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex justify-between items-baseline mb-12">
            <div className="v2-eyebrow text-[#0A2540]/60">
              <span className="v2-rule" /> Services in Focus
            </div>
          </div>
        </Reveal>

        <div className="space-y-20 md:space-y-32">
          {spots.map((s, i) => (
            <Reveal key={i} y={48} duration={0.9}>
              <article
                data-testid={`v2-spot-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start ${
                  i % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`lg:col-span-5 ${
                    i % 2 === 1 ? "lg:order-2" : ""
                  } v2-img-hover relative overflow-hidden`}
                >
                  <div className="aspect-[4/5] relative">
                    <img
                      src={s.img}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2540]/55 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 v2-eyebrow text-white text-[10px]">
                      <span className="v2-rule" />
                      {s.label}
                    </div>
                    <div className="absolute top-6 right-6 v2-slide-num text-white/70">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                </div>
                <div
                  className={`lg:col-span-7 ${
                    i % 2 === 1 ? "lg:order-1" : ""
                  }`}
                >
                  <div className="v2-eyebrow text-[#3B82F6] mb-4">
                    {s.kicker}
                  </div>
                  <h3
                    className="v2-h2 text-[#0A2540] whitespace-pre-line"
                    style={{ fontSize: "clamp(2.4rem, 4.6vw, 4.2rem)" }}
                  >
                    {s.title}
                  </h3>
                  <p className="v2-body text-[#0A2540]/75 mt-6 max-w-xl">
                    {s.body}
                  </p>

                  {/* Full service checklist — pulled from the deck */}
                  <ul
                    data-testid={`v2-spot-bullets-${i}`}
                    className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
                  >
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[15px] md:text-[16px] text-[#0A2540] leading-snug"
                      >
                        <span className="mt-[3px] shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600">
                          <Check size={13} strokeWidth={3} />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Add-ons — Advisory & Strategy only */}
                  {s.addons && (
                    <div
                      className="mt-8 border-t border-[#0A2540]/10 pt-6"
                      data-testid={`v2-spot-addons-${i}`}
                    >
                      <div className="v2-eyebrow text-[#3B82F6] text-[11px] mb-3">
                        Add-Ons
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {s.addons.map((a) => (
                          <span
                            key={a}
                            className="inline-flex items-center gap-2 text-[14px] md:text-[15px] text-[#0A2540]/80"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] inline-block" />
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <a
                    href="#contact"
                    className="v2-btn v2-btn-solid mt-10"
                    data-testid={`v2-spot-cta-${i}`}
                  >
                    Talk to a CFO <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};


// ============ SLIDE 16 — ACCOUNTANT VS CFO ============
const ComparisonV2 = () => {
  const rows = [
    ["FOCUS ON SAVING TAX", "FOCUS ON PROFITABILITY & CASHFLOW"],
    ["COMPLIANCE APPROACH", "COMMERCIAL APPROACH"],
    ["IS REACTIVE", "IS PROACTIVE"],
    ["TELLS YOU WHAT HAPPENED", "ADVISES WHAT YOU SHOULD DO"],
    ["DEPENDS ON HISTORICAL DATA", "STRATEGIC · ANALYTICAL · FORWARD-THINKING"],
  ];
  return (
    <section
      data-testid="v2-comparison"
      className="bg-white py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-[#0A2540]/60">
            <span className="v2-rule" /> The Envisio Difference
          </div>
        </div>
        <h2 className="v2-h2 text-[#0A2540] mb-4">ACCOUNTANT VS CFO.</h2>
        <p className="v2-body text-[#0A2540]/75 max-w-xl mb-16">
          A traditional accountant looks backward at compliance. A CFO looks
          forward at strategy. Here's the difference an Envisio engagement
          makes.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
          {/* ACCOUNTANT — muted, red X */}
          <div className="border border-[#0A2540]/10 p-8 md:p-10 bg-[#F4F1EA] md:mt-6">
            <div className="flex items-center gap-3 pb-5 border-b border-[#0A2540]/15">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-500/10">
                <X size={20} className="text-red-500" strokeWidth={3} />
              </span>
              <div>
                <div className="v2-eyebrow text-[#0A2540]/45 text-[10px]">
                  THE SHOEBOX
                </div>
                <div className="v2-display text-[26px] md:text-[32px] text-[#0A2540]/65">
                  AN ACCOUNTANT
                </div>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {rows.map((r) => (
                <li
                  key={r[0]}
                  className="flex items-start gap-3 py-3 border-b border-[#0A2540]/10"
                >
                  <X
                    size={16}
                    strokeWidth={3}
                    className="text-red-500/80 mt-[3px] shrink-0"
                  />
                  <span className="v2-eyebrow text-[#0A2540]/55 text-[11px] leading-relaxed">
                    {r[0]}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* CFO — emphasised, thicker outline, green ticks, larger type */}
          <div className="border-[3px] border-[#3B82F6] p-8 md:p-12 bg-[#0A2540] text-white relative overflow-hidden -mt-4 md:mt-0 md:-ml-px shadow-[0_30px_60px_-20px_rgba(59,130,246,0.35)]">
            <div className="absolute inset-0 ea-grain opacity-30" />
            <div className="relative">
              <div className="flex items-center gap-3 pb-6 border-b border-white/20">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-400/50">
                  <Check size={22} className="text-emerald-400" strokeWidth={3} />
                </span>
                <div>
                  <div className="v2-eyebrow text-[#3B82F6] text-[10px]">
                    YOUR BUSINESS NAVIGATOR
                  </div>
                  <div className="v2-display text-[34px] md:text-[44px] text-white leading-none">
                    A CFO
                  </div>
                </div>
              </div>
              <ul className="mt-6 space-y-2">
                {rows.map((r) => (
                  <li
                    key={r[1]}
                    className="flex items-start gap-3 py-3 border-b border-white/15"
                  >
                    <Check
                      size={18}
                      strokeWidth={3}
                      className="text-emerald-400 mt-[2px] shrink-0"
                    />
                    <span className="v2-eyebrow text-white text-[13px] md:text-[14px] leading-relaxed">
                      {r[1]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ SLIDE 17 — FINAL CTA / CONTACT ============
const FinalCTAV2 = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    // honeypot — should stay empty; bots typically fill every input
    website: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Time the form was rendered — used as a simple bot check
  const [mountedAt] = useState(() => Date.now());

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

  const onSubmit = async (e) => {
    e.preventDefault();

    // --- Anti-spam checks (client-side, first line of defence) ---
    if (form.website) {
      // Honeypot filled — silently swallow (bot behaviour)
      setSent(true);
      return;
    }
    if (Date.now() - mountedAt < 3000) {
      toast.error("Please take a moment to review your message before sending.");
      return;
    }
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.message || form.message.trim().length < 10) {
      toast.error("Please write a short message (10+ characters).");
      return;
    }
    if (form.message.length > 4000) {
      toast.error("Please keep your message under 4,000 characters.");
      return;
    }
    const linkCount = (form.message.match(/https?:\/\//gi) || []).length;
    if (linkCount > 3) {
      toast.error("Please reduce the number of links in your message.");
      return;
    }

    // --- POST to the backend which persists + emails Bachir via Resend ---
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          message: form.message.trim(),
          website: form.website, // honeypot passthrough for server-side check
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "", website: "" });
      toast.success("Enquiry sent — a senior Envisio advisor will reply within one business day.");
    } catch (err) {
      toast.error(err.message || "Unable to send right now. Please email bachir@envisioadvisory.com.au.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="v2-final"
      className="relative bg-[#0A2540] text-white py-32 md:py-44 overflow-hidden"
    >
      <ParallaxBg src={IMG.heroCity} speed={0.18} className="opacity-25" />
      <div className="absolute inset-0 bg-[#0A2540]/70" />
      <div className="absolute inset-0 ea-grain opacity-50" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex justify-between items-baseline mb-12">
          <div className="v2-eyebrow text-white/60">
            <span className="v2-rule" /> Let's Talk Business
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-6">
            <h2 className="v2-display text-white text-[clamp(3rem,8vw,7rem)] leading-[0.9]">
              DO YOUR
              <br />
              FUTURE
              <br />
              <span className="text-[#3B82F6]">A FAVOUR.</span>
            </h2>
            <p className="v2-body-lg mt-10 text-white/85 max-w-md">
              Let's talk business. Tell us where you're headed and a senior
              Envisio advisor will respond within one business day with a
              tailored plan and a no-obligation discovery call.
            </p>
            <div className="mt-10 max-w-md">
              <div className="v2-eyebrow text-white/45 text-[10px] mb-2">
                Office
              </div>
              <div className="text-[16px] text-white/90 leading-relaxed">
                80 George St, Parramatta NSW
                <br />
                <span className="text-white/60">Ground Floor · Australia</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="bg-white text-[#0A2540] p-8 md:p-10">
              {sent ? (
                <div
                  className="py-12 text-center"
                  data-testid="v2-success"
                >
                  <div className="inline-flex w-14 h-14 bg-[#0A2540] text-white items-center justify-center mb-6">
                    <Check size={22} />
                  </div>
                  <div className="v2-display text-[32px] text-[#0A2540]">
                    ENQUIRY RECEIVED.
                  </div>
                  <p className="mt-4 text-[15px] text-[#0A2540]/65 max-w-sm mx-auto">
                    A senior advisor will reach out within one business day.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="v2-btn v2-btn-solid mt-8"
                    data-testid="v2-send-another"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="v2-form"
                  className="space-y-5"
                >
                  {/* Honeypot — hidden from users, filled by bots */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="website-hp">
                      Website (leave this field empty)
                    </label>
                    <input
                      type="text"
                      id="website-hp"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange}
                      data-testid="v2-honeypot"
                    />
                  </div>
                  <div className="v2-eyebrow text-[#3B82F6]">Quick enquiry</div>
                  <div className="v2-display text-[34px] md:text-[42px] text-[#0A2540] leading-none">
                    LET'S TALK
                    <br />
                    BUSINESS.
                  </div>
                  <p className="text-[14px] text-[#0A2540]/70 leading-relaxed">
                    Share a few details below and a senior Envisio advisor will
                    be in touch within one business day.
                  </p>
                  <V2Field
                    name="name"
                    label="FULL NAME *"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                  <V2Field
                    name="email"
                    label="EMAIL *"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                  <V2Field
                    name="company"
                    label="COMPANY"
                    value={form.company}
                    onChange={onChange}
                  />
                  <div>
                    <label className="v2-eyebrow text-[#0A2540]/65 text-[10px] mb-2 block">
                      MESSAGE *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={3}
                      value={form.message}
                      onChange={onChange}
                      data-testid="v2-msg"
                      className="w-full bg-transparent border-b border-[#0A2540]/30 focus:border-[#0A2540] outline-none text-[15px] py-2 resize-none"
                      placeholder="Tell us about your business..."
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="v2-submit"
                    disabled={submitting}
                    className="v2-btn v2-btn-solid w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send Enquiry"}
                    <ArrowRight size={14} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const V2Field = ({ name, label, value, onChange, required, type = "text" }) => (
  <div>
    <label className="v2-eyebrow text-[#0A2540]/65 text-[10px] mb-2 block">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`v2-field-${name}`}
      className="w-full bg-transparent border-b border-[#0A2540]/30 focus:border-[#0A2540] outline-none text-[15px] py-2"
    />
  </div>
);

// ============ FOOTER ============
const FooterV2 = () => (
  <footer
    data-testid="v2-footer"
    data-logo-bg="dark"
    className="bg-black text-white/60 py-16"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo variant="white" height={64} />
          <p className="text-[14px] mt-6 max-w-sm text-white/55">
            Your virtual CFO — strategic finance for businesses that intend to
            grow. Award-winning. Australia-wide.
          </p>
          {/* Social links */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <a
              href="https://www.linkedin.com/company/envisioadvisory"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect with Envisio Advisory on LinkedIn"
              data-testid="v2-social-linkedin"
              className="group inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors"
            >
              <span className="w-10 h-10 border border-white/25 group-hover:bg-white group-hover:text-black group-hover:border-white flex items-center justify-center transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.339 18.338V9.75H5.667v8.588h2.672zM7.003 8.575a1.548 1.548 0 1 0 0-3.096 1.548 1.548 0 0 0 0 3.096zM18.338 18.338v-4.7c0-2.316-1.253-3.394-2.924-3.394-1.348 0-1.951.742-2.287 1.263V9.75h-2.672c.035.754 0 8.588 0 8.588h2.672v-4.796c0-.24.017-.48.088-.652.192-.48.632-.977 1.371-.977.967 0 1.353.737 1.353 1.818v4.607h2.399z"/></svg>
              </span>
              <span className="v2-eyebrow text-[11px] text-white/80 group-hover:text-white">
                Connect with us
              </span>
            </a>
            <a
              href="https://www.facebook.com/envisioadvisory"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Envisio Advisory on Facebook"
              data-testid="v2-social-facebook"
              className="w-10 h-10 border border-white/25 hover:bg-white hover:text-black hover:border-white flex items-center justify-center transition-colors text-white/80"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5 21v-7.5h2.55l.38-2.97h-2.93V8.62c0-.86.24-1.44 1.47-1.44h1.57V4.53a20.7 20.7 0 0 0-2.29-.12c-2.27 0-3.83 1.39-3.83 3.94v2.18H7.85v2.97h2.57V21h3.08z"/></svg>
            </a>
            <a
              href="https://www.instagram.com/envisioadvisory/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Envisio Advisory on Instagram"
              data-testid="v2-social-instagram"
              className="w-10 h-10 border border-white/25 hover:bg-white hover:text-black hover:border-white flex items-center justify-center transition-colors text-white/80"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
            </a>
          </div>
          {/* Professional credentials */}
          <div
            data-testid="v2-footer-credentials"
            className="mt-8 flex flex-col gap-4 items-start"
          >
            <div className="v2-eyebrow text-white/45 text-[10px]">
              Accredited Members
            </div>
            <img
              src="/img/credentials/cpa-australia.webp"
              alt="CPA Australia — Certified Practising Accountants"
              loading="lazy"
              className="h-16 w-auto object-contain"
            />
            <img
              src="/img/credentials/ipa-dark.png"
              alt="Institute of Public Accountants (IPA)"
              loading="lazy"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8 text-[13px]">
          <div>
            <div className="v2-eyebrow text-white/45 text-[10px] mb-4">
              Navigate
            </div>
            <ul className="space-y-2">
              <li><a href="#solutions" className="hover:text-white">Solutions</a></li>
              <li><a href="#process" className="hover:text-white">Process</a></li>
              <li><a href="#team" className="hover:text-white">Team</a></li>
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="v2-eyebrow text-white/45 text-[10px] mb-4">
              Visit
            </div>
            <ul className="space-y-2">
              <li>80 George St, Parramatta NSW</li>
              <li className="text-white/50">Ground Floor · Australia</li>
            </ul>
          </div>
          <div>
            <div className="v2-eyebrow text-white/45 text-[10px] mb-4">
              Services
            </div>
            <ul className="space-y-2">
              <li>Business Advisory</li>
              <li>Business Strategy</li>
              <li>Accounting &amp; Auditing</li>
              <li>Staffing &amp; Payroll</li>
              <li>Finance &amp; Modelling</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-[12px] text-white/40">
        <div>© {new Date().getFullYear()} Envisio Advisory Pty Ltd. All rights reserved.</div>
        <div className="v2-eyebrow text-white/40 text-[10px]">
          Award-winning virtual CFO
        </div>
      </div>
    </div>
  </footer>
);

// ============ PAGE ============
export default function V2() {
  return (
    <main data-testid="v2-page" className="v2 bg-white">
      <NavbarV2 />
      <HeroV2 />
      <QuoteV2 />
      <AboutV2 />
      <StatsV2 />
      <OneFirmV2 />
      <NavigatorsV2 />
      <ClientsV2 />
      <AwardsV2 />
      <MediaV2 />
      <PartnersV2 />
      <TestimonialsV2 />
      <TeamV2 />
      <MetricsV2 />
      <BenefitsV2 />
      <WhatWeDoV2 />
      <ServiceSpotlightsV2 />
      <ComparisonV2 />
      <FinalCTAV2 />
      <FooterV2 />
    </main>
  );
}
